// The design language, as data. **Zero imports, and that is the rule.**
//
// The render layer reads this file directly (PLAN-UI §28.1), and `src/client/ai/render/`
// may not pull in React or MUI — so a single import here would drag the whole
// component library into a Three.js module. Tokens are inert values; anything
// that needs a `Theme` reads `kit/theme.ts` instead.
//
// The values are copied from `docs/ui/kit-sheet.html`, which is the reference
// the designer edits. `tokens.test.ts` parses that sheet's `:root` block and
// fails on any disagreement, for the reason `theme.test.ts` once held the
// legacy mirror to `palette.css`: two sources of colour truth drift.

export const TOKENS = {
  color: {
    /** Warm charcoal — the page behind everything. */
    ground: "#191a1e",
    /** Resting surface. */
    panel: "#24252b",
    /** Raised surface: tiles, cards, plates. */
    panelRaised: "#2d2e36",
    /** Gunmetal border. */
    line: "#45464f",
    /** Border, lit. */
    lineHi: "#5b5c66",
    /** Primary text — warm off-white. */
    ink: "#e8e4d9",
    /** Secondary text. */
    inkDim: "#97927f",
    /** THE accent: actions, selection, score. */
    brand: "#fc9803",
    /** Pressed / darkened brand. */
    brandDown: "#c97a02",
    /** Bone — secondary emphasis, from the logo. */
    cream: "#f0e6cf",
    /** Refusal / danger. */
    rust: "#c9503a",

    // The seven below are declared by the sheet's *button and plate rules*
    // rather than its `:root` block — a hard drop-shadow, a hover lift and the
    // three "text on a filled button" colours. They are language, not one
    // component's detail, so they are named here; `tokens.test.ts` exempts
    // them from the `:root` comparison because there is nothing to compare to.
    /** The hard drop-shadow under a button or tile. */
    shadow: "#101114",
    brandHover: "#ffa81e",
    creamEdge: "#b8ad92",
    rustEdge: "#94341f",
    /** Text on a brand fill. */
    onBrand: "#241500",
    /** Text on a cream fill. */
    onCream: "#33301f",
    /** Text on a rust fill. */
    onRust: "#ffe9e2"
  },

  // **Cards only.** These five colours appear on cards and nowhere else
  // (PLAN-UI §28.2) — a rarity hue used as a general accent stops meaning
  // rarity, which is the one thing it is for.
  rarity: {
    common: "#ffffff",
    uncommon: "#03fc45",
    rare: "#0390fc",
    epic: "#be03fc",
    legend: "#fc9803"
  },

  // **Role tokens, and the faces are PLACEHOLDERS** (PLAN-UI §28.1). Each one
  // swaps in a single theme line when the designer names a face; never
  // hardcode a family outside the theme. `display` carries tabular numerals.
  //
  // No font files are loaded in U1 — loading lands with the first component
  // that uses a role, so nothing here costs a request yet.
  font: {
    heading: '"Changa One", "Arial Black", sans-serif',
    body: '"Rubik", "Segoe UI", sans-serif',
    display: '"Chakra Petch", "Consolas", monospace'
  },

  radius: { sm: 7, md: 10, card: 12, pill: 999 },

  // A starting scale on a 4px unit, extended as extraction demands. Named
  // rather than numbered so a gap can be inserted without renumbering.
  space: { xs: 4, sm: 8, md: 12, lg: 18, xl: 24 },

  // **The one z-scale** (PLAN-UI §28.1). No literal z-indexes in kit-built
  // components — a layer invented at a call site is a layer nobody can order
  // against the others.
  //
  // **MUI's own ladder is set from this one** (`theme.ts`), which is the U2
  // reconciliation. MUI ships appBar 1100, drawer 1200, modal 1300, snackbar
  // 1400, tooltip 1500 — numbers in the same range as these and in a different
  // order, so a legacy `Dialog` at 1300 buried a kit popover at 1200 with
  // nothing in either file saying why. Two ladders in one document is not a
  // conflict anything reports; it is a component that is simply behind another
  // one. `tokens.test.ts` pins the mapping so they cannot drift apart again.
  //
  // The rungs are a hundred apart on purpose: sorting *within* a layer —
  // board overlays depth-sorted by screen-y — is an offset that must not reach
  // the next rung, and `BoardAnchored` derives the ceiling from the gap rather
  // than repeating it.
  z: {
    boardOverlay: 1000,
    chrome: 1100,
    popover: 1200,
    pill: 1300,
    banner: 1400,
    pause: 1500,
    modal: 1600,
    dev: 1700,
    /**
     * Above everything, `dev` included — a tooltip explaining a control is
     * useless underneath the surface that control is on, and MUI's default
     * ladder puts tooltips at the top for the same reason.
     */
    tooltip: 1800
  },

  // The sheet's `.16s` hover/press and `.22s` card draw, in ms.
  //
  // `prefers-reduced-motion` is respected globally by the theme and the kit,
  // never here: tokens are inert data and cannot ask the browser anything.
  motion: {
    easeBounce: "cubic-bezier(.34,1.56,.64,1)",
    duration: { fast: 160, draw: 220 }
  },

  // Foundations only (PLAN-UI §28.1). Every zone carries a collapse behaviour
  // so a later mobile pass is a *layout* pass rather than a rebuild.
  breakpoint: { mobile: 480, tablet: 768, wide: 1080 },

  /** Minimum hit size in px — the mobile foundation line. */
  touchTarget: 44,

  // The one number `layout.ts` had left when the dock died (PLAN-UI §28.5, U8):
  // the sticky admin bar's height on `/tuner` and the feedback board, which
  // those two pages inset their canvas by. The game screen has no header — its
  // chrome measures itself into the inset registry.
  layout: { adminBar: 56 }
} as const;
